import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import {
  AccessGrantObject,
  EmailDeliveryMethod,
} from '$adminSrc/__generated__/graphql'

import { client } from '../../../../apollo'
import { CREATE_TENANT } from '../../../../graphql/tenant'

type FormState = {
  id: string
  name: string
  fullAccessGrantObjects: AccessGrantObject[] | null
  trialAccessGrantObjects: AccessGrantObject[] | null
  previewAccessGrantObjects: AccessGrantObject[] | null
  supportEmail: string | null
  faqUrl: string | null
  extensionInstallUrl: string | null
  hasOauthLogin: boolean
  hasEmailLogin: boolean
  emailDeliveryMethod: EmailDeliveryMethod
  emailCioAppApiKey: string | null
  emailCioTrackApiKey: string | null
  emailCioTrackSiteId: string | null
  emailCioWebhookSigningKey: string | null
  emailCioReportingWebhookSigningKey: string | null
  emailDeliveryWebhookUrl: string | null
  emailDeliveryWebhookRequestHeaders: string | null
  emailDeliveryWebhookTimeout: number | null
}

const ACCESS_GRANT_OPTIONS = [
  { value: AccessGrantObject.Account, label: 'Account' },
  { value: AccessGrantObject.Subscription, label: 'Subscription' },
  { value: AccessGrantObject.EnterprisePackage, label: 'Enterprise Package' },
]

enum SpecialOption {
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  ALWAYS_AVAILABLE = 'ALWAYS_AVAILABLE',
}

export default function CreateTenantForm() {
  const [formData, setFormData] = useState<FormState>({
    id: '',
    name: '',
    fullAccessGrantObjects: null,
    trialAccessGrantObjects: null,
    previewAccessGrantObjects: null,
    supportEmail: '',
    faqUrl: '',
    extensionInstallUrl: '',
    hasOauthLogin: false,
    hasEmailLogin: false,
    emailDeliveryMethod: EmailDeliveryMethod.Customerio,
    emailCioAppApiKey: undefined,
    emailCioTrackApiKey: undefined,
    emailCioTrackSiteId: undefined,
    emailCioWebhookSigningKey: undefined,
    emailCioReportingWebhookSigningKey: undefined,
    emailDeliveryWebhookUrl: undefined,
    emailDeliveryWebhookRequestHeaders: undefined,
    emailDeliveryWebhookTimeout: undefined,
  })

  function updateFormData(o: Partial<FormState>) {
    setFormData((prev) => ({ ...prev, ...o }))
  }

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [tenantId, setTenantId] = useState<string | null>(null)

  const [createTenant, { loading }] = useMutation(CREATE_TENANT, {
    onCompleted: (data) => {
      const result = data.adminCreateTenant
      if (result.statusCode === 200) {
        setSuccess(true)
        setTenantId(result.tenantId)
      } else {
        setError(result.error || 'An unknown error occurred')
      }
    },
    onError: (err) => {
      setError(err.message || 'Failed to create tenant')
    },
    client,
  })

  // Used for the "id" and "name" fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    updateFormData({ [name]: value })
  }

  // Used for the selection of multiple grant objects
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, selectedOptions } = e.target

    const selectedOptionsArray = Array.from(
      selectedOptions,
      (option) => option.value,
    )

    updateFormData({ [name]: selectedOptionsArray })
  }

  // Used to select the "always available" option
  // Used to select the "unavailable" option
  const handleSelectRadioAccessGrants = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target

    const key = name.replace('-special', '')

    if (value === SpecialOption.NOT_AVAILABLE) {
      updateFormData({ [key]: null })
    } else if (value === SpecialOption.ALWAYS_AVAILABLE) {
      updateFormData({ [key]: [] })
    } else {
      // pass
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    try {
      const emailConfig = {
        emailCioAppApiKey: formData.emailCioAppApiKey,
        emailCioTrackApiKey: formData.emailCioTrackApiKey,
        emailCioTrackSiteId: formData.emailCioTrackSiteId,
        emailCioWebhookSigningKey: formData.emailCioWebhookSigningKey,
        emailCioReportingWebhookSigningKey:
          formData.emailCioReportingWebhookSigningKey,
        emailDeliveryWebhookUrl: formData.emailDeliveryWebhookUrl,
        emailDeliveryWebhookRequestHeaders:
          formData.emailDeliveryWebhookRequestHeaders,
        emailDeliveryWebhookTimeout: formData.emailDeliveryWebhookTimeout,
      }
      if (formData.emailDeliveryMethod === EmailDeliveryMethod.Blackhole) {
        emailConfig.emailCioAppApiKey = null
        emailConfig.emailCioTrackApiKey = null
        emailConfig.emailCioTrackSiteId = null
        emailConfig.emailCioWebhookSigningKey = null
        emailConfig.emailCioReportingWebhookSigningKey = null
      }

      if (formData.emailDeliveryMethod !== EmailDeliveryMethod.Webhook) {
        emailConfig.emailDeliveryWebhookUrl = null
        emailConfig.emailDeliveryWebhookRequestHeaders = null
        emailConfig.emailDeliveryWebhookTimeout = null
      }

      await createTenant({
        variables: {
          id: formData.id,
          name: formData.name,
          fullAccessGrantObjects: {
            accessGrantObjects: formData.fullAccessGrantObjects,
            notAvailable: formData.fullAccessGrantObjects === null,
          },
          trialAccessGrantObjects: {
            accessGrantObjects: formData.trialAccessGrantObjects,
            notAvailable: formData.trialAccessGrantObjects === null,
          },
          previewAccessGrantObjects: {
            accessGrantObjects: formData.previewAccessGrantObjects,
            notAvailable: formData.previewAccessGrantObjects === null,
          },
          supportEmail: formData.supportEmail,
          faqUrl: formData.faqUrl,
          extensionInstallUrl: formData.extensionInstallUrl,
          hasOauthLogin: formData.hasOauthLogin,
          hasEmailLogin: formData.hasEmailLogin,
          emailDeliveryMethod: formData.emailDeliveryMethod,
          ...emailConfig,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }

  return (
    <div>
      {success && (
        <div>
          Tenant created successfully!
          {tenantId && <p>Tenant ID: {tenantId}</p>}
        </div>
      )}

      {error && <div>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="id">
            <strong>Tenant ID</strong>
            <br />
            <input
              type="text"
              id="id"
              name="id"
              value={formData.id}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="name">
            <strong>Tenant Name</strong>
            <br />
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div>
          <label htmlFor="fullAccessGrantObjects">
            <strong>Full Access Grant Objects</strong>
            <br />
            <small>Hold Ctrl/Cmd to select multiple options</small>
            <br />
            <select
              id="fullAccessGrantObjects"
              name="fullAccessGrantObjects"
              value={formData.fullAccessGrantObjects || []}
              onChange={handleSelectChange}
              multiple
            >
              {ACCESS_GRANT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <br />
          <label htmlFor="fullAccessGrantObjects-special-notAvailable">
            <input
              type="radio"
              id="fullAccessGrantObjects-special-notAvailable"
              name="fullAccessGrantObjects-special"
              onChange={handleSelectRadioAccessGrants}
              value={SpecialOption.NOT_AVAILABLE}
              checked={formData.fullAccessGrantObjects === null}
            />
            Unavailable
          </label>
          <br />
          <label htmlFor="fullAccessGrantObjects-special-alwaysAvailable">
            <input
              type="radio"
              id="fullAccessGrantObjects-special-alwaysAvailable"
              name="fullAccessGrantObjects-special"
              onChange={handleSelectRadioAccessGrants}
              value={SpecialOption.ALWAYS_AVAILABLE}
              checked={formData.fullAccessGrantObjects?.length === 0}
            />
            Always available
          </label>
        </div>

        <div>
          <label htmlFor="trialAccessGrantObjects">
            <strong>Trial Access Grant Objects</strong>
            <br />
            <small>Hold Ctrl/Cmd to select multiple options</small>
            <br />
            <select
              id="trialAccessGrantObjects"
              name="trialAccessGrantObjects"
              value={formData.trialAccessGrantObjects || []}
              onChange={handleSelectChange}
              multiple
            >
              {ACCESS_GRANT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <br />
          <label htmlFor="trialAccessGrantObjects-special-notAvailable">
            <input
              type="radio"
              id="trialAccessGrantObjects-special-notAvailable"
              name="trialAccessGrantObjects-special"
              onChange={handleSelectRadioAccessGrants}
              value={SpecialOption.NOT_AVAILABLE}
              checked={formData.trialAccessGrantObjects === null}
            />
            Unavailable
          </label>
          <br />
          <label htmlFor="trialAccessGrantObjects-special-alwaysAvailable">
            <input
              type="radio"
              id="trialAccessGrantObjects-special-alwaysAvailable"
              name="trialAccessGrantObjects-special"
              onChange={handleSelectRadioAccessGrants}
              value={SpecialOption.ALWAYS_AVAILABLE}
              checked={formData.trialAccessGrantObjects?.length === 0}
            />
            Always available
          </label>
        </div>

        <div>
          <label htmlFor="previewAccessGrantObjects">
            <strong>Preview Access Grant Objects</strong>
            <br />
            <small>Hold Ctrl/Cmd to select multiple options</small>
            <br />
            <select
              id="previewAccessGrantObjects"
              name="previewAccessGrantObjects"
              value={formData.previewAccessGrantObjects || []}
              onChange={handleSelectChange}
              multiple
            >
              {ACCESS_GRANT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <br />
          <label htmlFor="previewAccessGrantObjects-special-notAvailable">
            <input
              type="radio"
              id="previewAccessGrantObjects-special-notAvailable"
              name="previewAccessGrantObjects-special"
              onChange={handleSelectRadioAccessGrants}
              value={SpecialOption.NOT_AVAILABLE}
              checked={formData.previewAccessGrantObjects === null}
            />
            Unavailable
          </label>
          <br />
          <label htmlFor="previewAccessGrantObjects-special-alwaysAvailable">
            <input
              type="radio"
              id="previewAccessGrantObjects-special-alwaysAvailable"
              name="previewAccessGrantObjects-special"
              onChange={handleSelectRadioAccessGrants}
              value={SpecialOption.ALWAYS_AVAILABLE}
              checked={formData.previewAccessGrantObjects?.length === 0}
            />
            Always available
          </label>
        </div>
        <div>
          <strong>Relevant emails and URLs</strong>
          <br />
          <label htmlFor="supportEmail">
            Support email &nbsp;
            <input
              type="text"
              id="supportEmail"
              name="supportEmail"
              value={formData.supportEmail}
              onChange={handleInputChange}
              required
            />
          </label>
          <br />
          <label htmlFor="faqUrl">
            FAQ URL &nbsp;
            <input
              type="text"
              id="faqUrl"
              name="faqUrl"
              value={formData.faqUrl}
              onChange={handleInputChange}
              required
            />
          </label>
          <br />
          <label htmlFor="extensionInstallUrl">
            Extension install URL &nbsp;
            <input
              type="text"
              id="extensionInstallUrl"
              name="extensionInstallUrl"
              value={formData.extensionInstallUrl}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>
        <div>
          <strong>Available login methods</strong>
          <br />
          <label htmlFor="has-oauth-login">
            <input
              type="checkbox"
              id="has-oauth-login"
              name="login-method"
              checked={formData.hasOauthLogin}
              onChange={(e) => {
                updateFormData({ hasOauthLogin: e.target.checked })
              }}
            />
            Has OAuth login
          </label>
          <br />
          <label htmlFor="has-email-login">
            <input
              type="checkbox"
              id="has-email-login"
              name="login-method"
              checked={formData.hasEmailLogin}
              onChange={(e) => {
                updateFormData({ hasEmailLogin: e.target.checked })
              }}
            />
            Has email login
          </label>
          <br />
        </div>
        <div>
          <strong>Email delivery method</strong>
          <br />
          <label htmlFor="email-delivery-method-customerio">
            <input
              type="radio"
              id="email-delivery-method-customerio"
              name="email-delivery-method"
              value={EmailDeliveryMethod.Customerio}
              checked={
                formData.emailDeliveryMethod === EmailDeliveryMethod.Customerio
              }
              onChange={(e) => {
                if (e.target.checked) {
                  updateFormData({
                    emailDeliveryMethod: EmailDeliveryMethod.Customerio,
                  })
                }
              }}
            />
            Customer.io
          </label>
          <br />
          <label htmlFor="email-delivery-method-webhook">
            <input
              type="radio"
              id="email-delivery-method-webhook"
              name="email-delivery-method"
              value={EmailDeliveryMethod.Webhook}
              checked={
                formData.emailDeliveryMethod === EmailDeliveryMethod.Webhook
              }
              onChange={(e) => {
                if (e.target.checked) {
                  updateFormData({
                    emailDeliveryMethod: EmailDeliveryMethod.Webhook,
                  })
                }
              }}
            />
            Webhook
          </label>
          <br />
          <label htmlFor="email-delivery-method-blackhole">
            <input
              type="radio"
              id="email-delivery-method-blackhole"
              name="email-delivery-method"
              value={EmailDeliveryMethod.Blackhole}
              checked={
                formData.emailDeliveryMethod === EmailDeliveryMethod.Blackhole
              }
              onChange={(e) => {
                if (e.target.checked) {
                  updateFormData({
                    emailDeliveryMethod: EmailDeliveryMethod.Blackhole,
                  })
                }
              }}
            />
            Blackhole
          </label>
        </div>
        {formData.emailDeliveryMethod !== EmailDeliveryMethod.Blackhole && (
          <div>
            <strong>Customer.io Configuration</strong>
            <br />
            <label htmlFor="email-cio-app-api-key">
              App API key &nbsp;
              <input
                type="text"
                id="email-cio-app-api-key"
                name="email-cio-app-api-key"
                value={formData.emailCioAppApiKey}
                onChange={(e) => {
                  updateFormData({ emailCioAppApiKey: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-cio-track-api-key">
              Track API key &nbsp;
              <input
                type="text"
                id="email-cio-track-api-key"
                name="email-cio-track-api-key"
                value={formData.emailCioTrackApiKey}
                onChange={(e) => {
                  updateFormData({ emailCioTrackApiKey: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-cio-track-site-id">
              Track site id &nbsp;
              <input
                type="text"
                id="email-cio-track-site-id"
                name="email-cio-track-site-id"
                value={formData.emailCioTrackSiteId}
                onChange={(e) => {
                  updateFormData({ emailCioTrackSiteId: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-cio-webhook-signing-key">
              Webhook signing key &nbsp;
              <input
                type="text"
                id="email-cio-webhook-signing-key"
                name="email-cio-webhook-signing-key"
                value={formData.emailCioWebhookSigningKey}
                onChange={(e) => {
                  updateFormData({ emailCioWebhookSigningKey: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-cio-reporting-webhook-signing-key">
              Reporting webhook signing key &nbsp;
              <input
                type="text"
                id="email-cio-reporting-webhook-signing-key"
                name="email-cio-reporting-webhook-signing-key"
                value={formData.emailCioReportingWebhookSigningKey}
                onChange={(e) => {
                  updateFormData({
                    emailCioReportingWebhookSigningKey: e.target.value,
                  })
                }}
              />
            </label>
          </div>
        )}
        {formData.emailDeliveryMethod === EmailDeliveryMethod.Webhook && (
          <div>
            <strong>Email Webhook Configuration</strong>
            <br />
            <label htmlFor="email-delivery-webhook-url">
              Webhook URL &nbsp;
              <input
                type="text"
                id="email-delivery-webhook-url"
                name="email-delivery-webhook-url"
                value={formData.emailDeliveryWebhookUrl}
                onChange={(e) => {
                  updateFormData({ emailDeliveryWebhookUrl: e.target.value })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-delivery-webhook-request-headers">
              Request headers (as JSON object) &nbsp;
              <input
                type="text"
                id="email-delivery-webhook-request-headers"
                name="email-delivery-webhook-request-headers"
                value={formData.emailDeliveryWebhookRequestHeaders}
                onChange={(e) => {
                  updateFormData({
                    emailDeliveryWebhookRequestHeaders: e.target.value,
                  })
                }}
              />
            </label>
            <br />
            <label htmlFor="email-delivery-webhook-timeout">
              Webhook timeout (integer) &nbsp;
              <input
                type="number"
                id="email-delivery-webhook-timeout"
                name="email-delivery-webhook-timeout"
                value={formData.emailDeliveryWebhookTimeout}
                onChange={(e) => {
                  updateFormData({
                    emailDeliveryWebhookTimeout: parseInt(e.target.value, 10),
                  })
                }}
              />
            </label>
          </div>
        )}
        <hr />
        <button type="submit" disabled={loading}>
          {loading ? 'Creating...' : 'Create Tenant'}
        </button>
      </form>
    </div>
  )
}
